import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import {
  directedClipKeys,
  directedFootageSec,
  directedPriceCredits,
  type DirectedScript,
  type DirectedShot,
} from 'fym-shared/src/music24/directedVideoApi';
import { aiClipCreditsFor } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { useIsPhoneLayout } from '../../musicVideoEditor/phone/useIsPhoneLayout';
import { formatTime } from '../../musicVideoEditor/timeline/timelineGeometry';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { scriptMessages as messages } from '../messages/scriptMessages';
import { DirectedStepLayout } from '../shell/DirectedStepLayout';
import { sectionAt, sectionDisplayName, sectionToneOf } from '../shell/sectionBar';
import { AlertIcon } from './director/AlertIcon';
import { DirectorNotices } from './director/DirectorNotices';
import { DirectorWorking } from './DirectorWorking';
import { PhoneShotList } from './script/PhoneShotList';
import { PhoneShotSheet } from './script/PhoneShotSheet';
import { ScriptSide } from './script/ScriptSide';
import {
  clipNumbers,
  clipSecOfShot,
  clipStillsOf,
  effectsOfShot,
  hasOwnClip,
  insertShotAfter,
  isClipStart,
  removeShot,
  replaceShot,
  shotForSave,
  shotTextOf,
  type ShotText,
} from './script/scriptEdits';
import { ShotEditor } from './script/ShotEditor';
import { useScriptActions } from './script/useScriptActions';
import { ScriptTimeline } from './ScriptTimeline';
import { ShotRow } from './ShotRow';
import { SparkleIcon } from './script/ShotIcons';
import { boldPart } from '../../common/richTextChildren';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import steps from './DirectorSteps.module.scss';
import styles from './script/Script.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

// A key from the text of the part: a message with several bold parts has no duplicate key (QA skpr2-3).
const bold = boldPart;

/** The shot open for edit, and the script that a save writes it into (with a new shot in it). */
interface Editing {
  shot: DirectedShot;
  script: DirectedScript;
  isNew: boolean;
  note: string;
}

/** A shot rewrite in progress. `seen`: the video was read with the director at work. */
interface Rewrite {
  shotId: string | null;
  note: string;
  seen: boolean;
}

/** The text of a save that a newer revision refused (design e2: "Put my text back"). */
interface StaleDraft {
  shotId: string;
  number: number;
  text: ShotText;
}

/** Shows the row of a shot in the list. */
function scrollToShot(shotId: string): void {
  document
    .querySelector(`[data-testid="shot-row-${shotId}"]`)
    ?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
}

/**
 * Step 4, Script (design directed-video e1, e2, j3): the shots against the song. The artist edits a
 * shot, removes it, adds one, or asks the director to write one shot or the whole script again.
 * Every save sends `PUT …/script` with the revision on screen; a 409 `M24_DIRECTED_STALE` reads the
 * script again and keeps the artist's text in a draft. "See the price" reads the price.
 */
const ScriptStepComponent: React.FC = () => {
  const intl = useIntl();
  const { accountId, catalogItemUuid, video, song, screen, currentScreen, readOnly, openStep } =
    useDirectedVideoContext();
  const isPhone = useIsPhoneLayout();
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, uuid: video.uuid }),
    [accountId, catalogItemUuid, video.uuid]
  );
  const actions = useScriptActions(args, video.scriptRevision);
  const stillOf = useMemo(() => clipStillsOf(video.clipEntries), [video.clipEntries]);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [rewrite, setRewrite] = useState<Rewrite | null>(null);
  const [staleDraft, setStaleDraft] = useState<StaleDraft | null>(null);
  const [editedIds, setEditedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [isRewriteAllOpen, setIsRewriteAllOpen] = useState(false);
  const [sheetShotId, setSheetShotId] = useState<string | null>(null);

  const isWorking = video.directorState === 'working';
  useEffect(() => {
    if (isWorking) {
      setRewrite((current) => (current === null || current.seen ? current : { ...current, seen: true }));
    } else {
      setRewrite((current) => (current?.seen === true ? null : current));
    }
  }, [isWorking]);

  const { script } = video;
  const noRuns = video.directorRunsLeft <= 0;
  const busy = readOnly || isWorking || actions.isRunning;
  const canRewrite = !busy && !noRuns;
  const isRevisit = screen !== currentScreen;
  const rewritingShotId = rewrite !== null && (isWorking || !rewrite.seen) ? rewrite.shotId : null;
  const showsWorking = isWorking && rewritingShotId === null;
  const listScript = editing?.isNew === true ? editing.script : script;
  const clipCount = script === null ? 0 : directedClipKeys(script).length;
  // Each new clip costs by its length (`aiClipCreditsFor`), so the line shows the seconds, not a price per clip.
  const priceTotal = script === null ? 0 : directedPriceCredits(script);
  const priceLine = intl.formatMessage(messages.priceLineSeconds, {
    count: clipCount,
    seconds: script === null ? 0 : directedFootageSec(script),
    total: priceTotal,
  });

  const startEdit = (shot: DirectedShot) => {
    if (script === null) {
      return;
    }
    setRemovingId(null);
    setStaleDraft(null);
    setEditing({ shot, script, isNew: false, note: '' });
    scrollToShot(shot.id);
  };

  const addAfter = (shotId: string) => {
    if (script === null) {
      return;
    }
    const insert = insertShotAfter(script, shotId);
    if (insert !== null) {
      setRemovingId(null);
      setEditing({ shot: insert.shot, script: insert.script, isNew: true, note: '' });
    }
  };

  const saveEditing = async () => {
    if (editing === null) {
      return;
    }
    const shot = shotForSave(editing.shot);
    const refusal = await actions.save(replaceShot(editing.script, shot));
    if (refusal === null) {
      setEditedIds((current) => new Set(current).add(shot.id));
      setEditing(null);
      return;
    }
    if (refusal === 'stale' && !editing.isNew) {
      const number = editing.script.shots.findIndex((item) => item.id === shot.id) + 1;
      setStaleDraft({ shotId: shot.id, number, text: shotTextOf(editing.shot) });
      setEditing(null);
    }
  };

  const putTextBack = () => {
    if (staleDraft === null || script === null) {
      return;
    }
    const current = script.shots.find((shot) => shot.id === staleDraft.shotId);
    if (current !== undefined) {
      setEditing({ shot: { ...current, ...staleDraft.text }, script, isNew: false, note: '' });
      scrollToShot(current.id);
    }
    setStaleDraft(null);
  };

  const confirmRemove = async (shotId: string) => {
    if (script === null) {
      return;
    }
    const removal = removeShot(script, shotId);
    if (removal === null) {
      return;
    }
    const refusal = await actions.save(removal.script);
    if (refusal === null || refusal === 'stale') {
      setRemovingId(null);
    }
  };

  const startRewrite = async (shotId: string, note: string) => {
    setEditing(null);
    setRewrite({ shotId, note, seen: false });
    const refusal = await actions.rewrite(shotId, note);
    if (refusal !== null) {
      setRewrite(null);
    }
  };

  const startRewriteAll = async (note: string) => {
    setIsRewriteAllOpen(false);
    setEditing(null);
    setRewrite(null);
    await actions.rewriteAll(note);
  };

  const seePrice = async () => {
    if (video.status === 'priced' && editing === null) {
      openStep(null);
      return;
    }
    const ok = await actions.seePrice();
    if (ok && isRevisit) {
      openStep(null);
    }
  };

  const removeLinesOf = (shotId: string): [string, string] => {
    if (script === null) {
      return ['', ''];
    }
    const number = script.shots.findIndex((shot) => shot.id === shotId) + 1;
    const removal = removeShot(script, shotId);
    const grown = removal?.grown ?? null;
    let ask: string;
    if (grown === null) {
      ask = intl.formatMessage(messages.removeAskOnly, { number });
    } else {
      const other = script.shots.findIndex((shot) => shot.id === grown.shotId) + 1;
      ask =
        grown.side === 'previous'
          ? intl.formatMessage(messages.removeAsk, {
              number,
              previous: other,
              from: formatTime(grown.startSec),
              to: formatTime(grown.endSec),
            })
          : intl.formatMessage(messages.removeAskFirst, { number, next: other, from: formatTime(grown.startSec) });
    }
    const removed = script.shots.find((shot) => shot.id === shotId);
    const price =
      removed !== undefined && hasOwnClip(script, shotId)
        ? intl.formatMessage(messages.removePrice, { credits: aiClipCreditsFor(clipSecOfShot(removed)) })
        : intl.formatMessage(messages.removeNoPrice);
    return [ask, price];
  };

  const editorOf = (current: Editing) => (
    <ShotEditor
      shot={current.shot}
      cast={video.cast}
      effects={effectsOfShot(current.script, current.shot)}
      isSaving={actions.isSaving}
      rewrite={
        current.isNew
          ? null
          : {
              note: current.note,
              onNote: (note) => setEditing({ ...current, note }),
              onRewrite: () => {
                startRewrite(current.shot.id, current.note).catch(console.error);
              },
              disabled: !canRewrite,
            }
      }
      onChange={(shot) => setEditing({ ...current, shot })}
      onSave={() => {
        saveEditing().catch(console.error);
      }}
      onCancel={() => setEditing(null)}
    />
  );

  const durationSec = song.durationSec ?? listScript?.shots.reduce((end, shot) => Math.max(end, shot.endSec), 0) ?? 1;
  let body: React.ReactNode;
  if (showsWorking) {
    body = <DirectorWorking kind="script" />;
  } else if (listScript === null) {
    body = (
      <div className={steps.work}>
        <p className={steps.workText}>{intl.formatMessage(messages.noScript)}</p>
        <SMLButton
          variant="primary"
          disabled={!canRewrite}
          onClick={() => {
            startRewriteAll('').catch(console.error);
          }}
        >
          {intl.formatMessage(messages.writeScript)}
        </SMLButton>
      </div>
    );
  } else if (isPhone) {
    body = (
      <PhoneShotList
        script={listScript}
        sections={song.sections}
        stillOf={stillOf}
        editedIds={editedIds}
        rewritingShotId={rewritingShotId}
        disabled={busy}
        onMore={setSheetShotId}
      />
    );
  } else {
    const numbers = clipNumbers(listScript);
    body = (
      <>
        <ScriptTimeline
          script={listScript}
          sections={song.sections}
          durationSec={durationSec}
          selectedShotId={editing?.shot.id ?? null}
          onShot={scrollToShot}
        />
        <div className={styles.shots}>
          {listScript.shots.map((shot, index) => {
            const section = sectionAt(song.sections, shot.startSec);
            let mode: 'view' | 'editing' | 'removing' | 'rewriting' = 'view';
            if (shot.id === rewritingShotId) {
              mode = 'rewriting';
            } else if (editing?.shot.id === shot.id) {
              mode = 'editing';
            } else if (removingId === shot.id) {
              mode = 'removing';
            }
            const shown = mode === 'editing' && editing !== null ? editing.shot : shot;
            return (
              <React.Fragment key={shot.id}>
                {index > 0 && editing === null && (
                  <div className={styles.addRow}>
                    <i aria-hidden />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => addAfter(listScript.shots[index - 1]?.id ?? '')}
                    >
                      {intl.formatMessage(messages.addHere)}
                    </button>
                    <i aria-hidden />
                  </div>
                )}
                <ShotRow
                  shot={shown}
                  number={index + 1}
                  clipNumber={numbers.get(shot.clipKey) ?? 1}
                  isClipStart={isClipStart(listScript, shot.id)}
                  section={
                    section === null
                      ? null
                      : { name: sectionDisplayName(section.label), tone: sectionToneOf(section.kind) }
                  }
                  cast={video.cast}
                  effects={effectsOfShot(listScript, shot)}
                  stillUrl={stillOf.get(shot.clipKey) ?? null}
                  mode={mode}
                  isEdited={editedIds.has(shot.id)}
                  editor={mode === 'editing' && editing !== null ? editorOf(editing) : undefined}
                  rewriteNote={rewrite?.note}
                  removeLines={mode === 'removing' ? removeLinesOf(shot.id) : undefined}
                  disabled={busy}
                  canRewrite={canRewrite}
                  canRemove={listScript.shots.length > 1 && editing?.isNew !== true}
                  onEdit={() => startEdit(shot)}
                  onRewrite={() => {
                    startRewrite(shot.id, '').catch(console.error);
                  }}
                  onRemove={() => {
                    setEditing(null);
                    setRemovingId(shot.id);
                  }}
                  onKeep={() => setRemovingId(null)}
                  onConfirmRemove={() => {
                    confirmRemove(shot.id).catch(console.error);
                  }}
                />
              </React.Fragment>
            );
          })}
        </div>
      </>
    );
  }

  const sheetShot = sheetShotId === null ? undefined : script?.shots.find((shot) => shot.id === sheetShotId);
  const shotCount = script?.shots.length ?? 0;
  const side =
    script === null ? undefined : (
      <ScriptSide
        script={script}
        isRewriteOpen={isRewriteAllOpen}
        rewriteDisabled={!canRewrite}
        onRewrite={(note) => {
          startRewriteAll(note).catch(console.error);
        }}
        onCloseRewrite={() => setIsRewriteAllOpen(false)}
      />
    );

  return (
    <DirectedStepLayout side={side}>
      <div className={clsx(shell.head, styles.editorLine)}>
        <div className={shell.grow}>
          <h2>{intl.formatMessage(messages.title)}</h2>
          {script !== null && (
            <p className={clsx(shell.sub, shell.subDesktop)}>
              {intl.formatMessage(staleDraft === null ? messages.intro : messages.introShort, { count: shotCount })}
            </p>
          )}
        </div>
        {script !== null && (
          <SMLButton
            className={steps.small}
            icon={<SparkleIcon />}
            disabled={!canRewrite}
            ariaLabel={intl.formatMessage(messages.rewriteAll)}
            onClick={() => setIsRewriteAllOpen(true)}
          >
            <span className={shell.desktopOnly}>{intl.formatMessage(messages.rewriteAll)}</span>
          </SMLButton>
        )}
      </div>
      <DirectorNotices
        kind="script"
        error={actions.error === 'stale' && staleDraft !== null ? null : actions.error}
        showRunFailure={!isRevisit}
        onRetry={() => {
          startRewriteAll('').catch(console.error);
        }}
      />
      {staleDraft !== null && (
        <div className={clsx(steps.alert, steps.alertInfo)} role="alert">
          <AlertIcon kind="info" />
          <div>
            {script?.shots.some((shot) => shot.id === staleDraft.shotId) === true ? (
              <>
                {intl.formatMessage(messages.stale, { b: bold, number: staleDraft.number })}
                <button type="button" className={steps.alertAction} onClick={putTextBack}>
                  {intl.formatMessage(messages.putTextBack)}
                </button>
              </>
            ) : (
              intl.formatMessage(messages.staleGone, { b: bold })
            )}
          </div>
        </div>
      )}
      {body}
      <div className={shell.foot}>
        <SMLButton className={clsx(steps.small, steps.ghost)} disabled={isWorking} onClick={() => openStep('style')}>
          {intl.formatMessage(messages.backToStyle)}
        </SMLButton>
        <span className={clsx(shell.grow, shell.desktopOnly)} />
        {script !== null && <span className={clsx(shell.footNote, shell.desktopOnly)}>{priceLine}</span>}
        <SMLButton
          variant="primary"
          disabled={busy || script === null || editing !== null || actions.isPricing}
          onClick={() => {
            seePrice().catch(console.error);
          }}
        >
          {isPhone
            ? intl.formatMessage(messages.seePricePhone, { total: priceTotal })
            : intl.formatMessage(messages.seePrice)}
        </SMLButton>
      </div>

      {isPhone && sheetShot !== undefined && (
        <PhoneShotSheet
          number={(script?.shots.indexOf(sheetShot) ?? 0) + 1}
          canRewrite={canRewrite}
          canRemove={shotCount > 1}
          onEdit={() => startEdit(sheetShot)}
          onRewrite={() => {
            startRewrite(sheetShot.id, '').catch(console.error);
          }}
          onAdd={() => addAfter(sheetShot.id)}
          onRemove={() => setRemovingId(sheetShot.id)}
          onClose={() => setSheetShotId(null)}
        />
      )}
      {isPhone && editing !== null && (
        <div className={clsx(styles.sheet, styles.sheetFull)} role="dialog" aria-modal="true">
          <h4>
            {intl.formatMessage(messages.sheetTitle, {
              number: editing.script.shots.findIndex((shot) => shot.id === editing.shot.id) + 1,
            })}
          </h4>
          {editorOf(editing)}
        </div>
      )}
      {isPhone && removingId !== null && (
        <ConfirmDialog
          isOpen
          tone="danger"
          title={removeLinesOf(removingId)[0]}
          description={removeLinesOf(removingId)[1]}
          confirmLabel={intl.formatMessage(messages.remove)}
          cancelLabel={intl.formatMessage(messages.keep)}
          isBusy={actions.isSaving}
          onConfirm={() => {
            confirmRemove(removingId).catch(console.error);
          }}
          onCancel={() => setRemovingId(null)}
        />
      )}
      {isPhone && isRewriteAllOpen && (
        <ConfirmDialog
          isOpen
          tone="neutral"
          title={intl.formatMessage(messages.rewriteAllTitle)}
          description={intl.formatMessage(messages.rewriteAllText)}
          confirmLabel={intl.formatMessage(messages.rewriteAllConfirm)}
          isConfirmDisabled={!canRewrite}
          onConfirm={() => {
            startRewriteAll('').catch(console.error);
          }}
          onCancel={() => setIsRewriteAllOpen(false)}
        />
      )}
    </DirectedStepLayout>
  );
};

export const ScriptStep = TypedMemo(ScriptStepComponent);
